import { memo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { vars } from 'src/core/themes';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { useListingAutoPricingSettings } from 'src/modals/ListingDetails/components/useListingAutoPricingSettings';
import { ProcessingIcon } from 'src/svgs/Viagogo';
import { STANDARD_DEVIATIONS_TO_CID } from 'src/utils/constants/contentIdMaps';
import { AutoPricingOutlierMode } from 'src/WebApiController';

import {
  ListingPricingInput,
  ListingPricingSettingsCellProps,
} from '../ListingPriceForm.types';
import { useCellDisabled, useIsSubmittingField } from '../useCellState';
import * as styles from './Cell.css';

const OutlierFilteringCellRaw: React.FC<ListingPricingSettingsCellProps> = ({
  listing,
  editable,
  event,
  isV2Variant = false,
}) => {
  const { watch, formState, getFieldState, setValue } =
    useFormContext<ListingPricingInput>();

  const {
    outlierMode,
    outlierStandardDeviations,
    canAutoPrice,
    onOutlierChange,
  } = useListingAutoPricingSettings(listing);

  const rowIndex = watch('rowIndex');
  const isDisabled = useCellDisabled(
    listing,
    canAutoPrice,
    editable,
    event.virtId,
    outlierMode !== AutoPricingOutlierMode.StandardDeviations
  );
  const isSubmittingField = useIsSubmittingField('outlierStandardDeviations');

  const onUpdateOutlierFilter = useCallback(
    (value: string | null) => {
      const sanitized = parseInt(value ?? '');
      if (value != null && !isNaN(sanitized)) {
        onOutlierChange({
          outlierModeNew: AutoPricingOutlierMode.StandardDeviations,
          outlierStandardDeviationsNew: sanitized,
        });
      } else {
        onOutlierChange({
          outlierModeNew: AutoPricingOutlierMode.StandardDeviations,
          outlierStandardDeviationsNew: null,
        });
      }
      // Only submit if Enter is hit in the box that has changes
      setValue('isSubmittingPricingSettings', true);
      setValue('isSubmittingRowIndex', rowIndex);
      setValue('submittingField', 'outlierStandardDeviations');
    },
    [onOutlierChange, rowIndex, setValue]
  );

  const content = (
      <PosFormField
        errors={
          getFieldState('outlierStandardDeviations', formState)?.error?.message
        }
        showErrorsInline
        className={styles.formFieldContainer}
      >
        {isSubmittingField ? (
          <div className="operation-button">
            <RotatingWrapper>
              <ProcessingIcon size={vars.iconSize.m} />
            </RotatingWrapper>
          </div>
        ) : (
          <PosEnumSelect
            className={isV2Variant ? styles.formSelectFieldV2 : styles.formSelectField}
            value={outlierStandardDeviations?.toString()}
            disabled={isDisabled}
            valueOptionsContent={STANDARD_DEVIATIONS_TO_CID}
            enableEmptySelection={true}
            onClick={(e) => e.stopPropagation()}
            onChange={onUpdateOutlierFilter}
          />
        )}
      </PosFormField>
  );

  return isV2Variant ? (
    content
  ) : (
    <TableCellDiv align="right" showTooltip={false}>
      {content}
    </TableCellDiv>
  );
};

export const OutlierFilteringCell = memo<ListingPricingSettingsCellProps>(
  OutlierFilteringCellRaw
);
